import {
  LoyaltyQuestIncentiveType,
  LoyaltyQuestStatus,
  LoyaltyQuestStepStatus,
} from 'generated/graphql-gateway';
import type { IMenuImageFragment } from 'generated/sanity-graphql';
import { t } from 'state/intl/translations';

import type { ICmsQuestCard, ICmsQuestCardUI, ICmsQuestDetail } from '../cms-hooks/types';
import { IQuest } from '../loyalty-quest/types';

import { formatQuestDates } from './date-formatter';

export const getButtonText = ({
  questStatus,
  incentiveRedeemed,
  cmsQuestCardUI,
}: {
  questStatus: LoyaltyQuestStatus | undefined;
  incentiveRedeemed: boolean;
  cmsQuestCardUI: ICmsQuestCardUI | null | undefined;
}): string => {
  let buttonText: string = '';

  switch (questStatus) {
    case LoyaltyQuestStatus.NOT_ENROLLED: {
      buttonText = cmsQuestCardUI?.notEnrolledButton?.locale ?? '';
      break;
    }
    case LoyaltyQuestStatus.ACTIVATED: {
      buttonText = cmsQuestCardUI?.enrolledButton?.locale ?? '';
      break;
    }
    case LoyaltyQuestStatus.COMPLETED: {
      if (!incentiveRedeemed) {
        buttonText = cmsQuestCardUI?.completedButton?.locale ?? '';
      }
      break;
    }
    default: {
      break;
    }
  }

  return buttonText;
};

export const getRangeDateDescription = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}): string => {
  const [formattedStartDate, formattedEndDate] = formatQuestDates(startDate, endDate);
  const description = `${t('from')} ${formattedStartDate} ${t('to')} ${formattedEndDate}`;

  return description;
};

export const getRedeemedDescription = ({
  incentiveType,
  cmsQuestDetail,
}: {
  incentiveType: LoyaltyQuestIncentiveType | undefined;
  cmsQuestDetail: ICmsQuestDetail | null;
}): string => {
  return (
    (incentiveType === LoyaltyQuestIncentiveType.BONUS_POINTS
      ? cmsQuestDetail?.completedAndRedeemedBonusPointChallengeDescription?.localeRaw
      : cmsQuestDetail?.completedAndRedeemedDescription?.localeRaw) || ''
  );
};

export const formatCompletedChallengeMessage = ({
  description,
  quest,
}: {
  description: string | null | undefined;
  quest: IQuest | undefined;
}): string =>
  description?.toString().replace('{points}', quest?.bonusPoints?.toString() || '0') || '';

export const getActiveTierIndex = (quest: IQuest | undefined): number | null => {
  const steps = quest?.steps;
  if (!steps?.length) {
    return null;
  }

  const firstNotCompleted = steps.find(s => s.status === LoyaltyQuestStepStatus.NOT_COMPLETED);

  return firstNotCompleted?.tierIndex ?? null;
};

export const getActiveTierImage = (
  quest: IQuest | undefined,
  cmsQuestCard: ICmsQuestCard | undefined,
  cmsQuestDetail: ICmsQuestDetail | null
): { image: IMenuImageFragment | null; imageDescription: string } => {
  const fallback = {
    image: cmsQuestCard?.image?.locale ?? null,
    imageDescription: cmsQuestCard?.imageDescription?.locale ?? '',
  };

  const cmsTiers = cmsQuestDetail?.questRules?.tiers;
  if (!cmsTiers?.length) {
    return fallback;
  }

  const activeTierIndex = getActiveTierIndex(quest);
  if (activeTierIndex == null) {
    if (quest?.status === LoyaltyQuestStatus.COMPLETED) {
      const lastTierImage = cmsTiers[cmsTiers.length - 1]?.headerImage?.locale ?? null;
      if (lastTierImage) {
        return {
          image: lastTierImage,
          imageDescription: lastTierImage.asset?.altText ?? fallback.imageDescription,
        };
      }
    }

    return fallback;
  }

  const tierImage = cmsTiers[activeTierIndex]?.headerImage?.locale ?? null;
  if (!tierImage) {
    return fallback;
  }

  return {
    image: tierImage,
    imageDescription: tierImage.asset?.altText ?? fallback.imageDescription,
  };
};

export const getActiveTierDescription = (
  quest: IQuest | undefined,
  cmsQuestDetail: ICmsQuestDetail | null
): any | null => {
  const cmsTiers = cmsQuestDetail?.questRules?.tiers;
  const fallback = cmsQuestDetail?.howToRedeem?.localeRaw ?? null;

  if (!cmsTiers?.length) {
    return fallback;
  }

  const activeTierIndex = getActiveTierIndex(quest);
  if (activeTierIndex == null) {
    return fallback;
  }

  return cmsTiers[activeTierIndex]?.description?.localeRaw ?? fallback;
};
